/**
 * Bluedot Redux Actions
 *
 * Async and sync actions for Bluedot SDK state management.
 */

import {
  BLUEDOT_INITIALIZE,
  BLUEDOT_INITIALIZE_SUCCESS,
  BLUEDOT_INITIALIZE_FAILURE,
  BLUEDOT_LOCATION_ENABLE,
  BLUEDOT_LOCATION_ENABLED,
  BLUEDOT_LOCATION_FAILURE,
  BLUEDOT_WAVE_SEND,
  BLUEDOT_WAVE_SENT,
  BLUEDOT_WAVE_FAILURE,
  BLUEDOT_RESET,
  BLUEDOT_ERROR,
  BLUEDOT_NATIVE_PERMISSION_CHECK,
  BLUEDOT_NATIVE_PERMISSION_GRANTED,
  BLUEDOT_NATIVE_PERMISSION_DENIED
} from "./constants";

import bluedotService from "../../services/bluedot";
import {
  initializeBluedotWithPermissionCheck,
  checkBrowserPermission,
  checkNativeLocationPermission,
  requestNativeLocationPermission,
  getBluedotIntegrationStatus
} from "../../utils/bluedotWebViewIntegration";
import bluedotConfig from "../../config/bluedot";
import { isWebview } from "../../utils/isWebview";

/**
 * Sync Redux state with actual Bluedot service state
 */
const syncWithServiceState = (dispatch, getState) => {
  const status = getBluedotIntegrationStatus();

  // Sync initialization state
  if (status.initialized) {
    dispatch({ type: BLUEDOT_INITIALIZE_SUCCESS });
  }

  // Sync permission state
  if (status.locationPermissionGranted) {
    dispatch({ type: BLUEDOT_NATIVE_PERMISSION_GRANTED });
  } else if (
    status.nativePermissionStatus &&
    status.nativePermissionStatus !== "unknown"
  ) {
    dispatch({
      type: BLUEDOT_NATIVE_PERMISSION_CHECK,
      payload: { status: status.nativePermissionStatus }
    });
  }

  return status;
};

/**
 * Initialize Bluedot SDK with permission-aware logic
 * Checks permissions first, then initializes SDK
 */
export const initializeBluedot = () => {
  return async (dispatch, getState) => {
    const state = getState();

    // Skip if disabled
    if (!bluedotConfig.enabled) {
      dispatch({ type: BLUEDOT_INITIALIZE_SUCCESS });
      return;
    }

    // Skip if already initialized
    if (state.bluedot && state.bluedot.initialized) {
      return;
    }

    dispatch({ type: BLUEDOT_INITIALIZE });

    try {
      const result = await initializeBluedotWithPermissionCheck();

      if (result.success) {
        dispatch({ type: BLUEDOT_INITIALIZE_SUCCESS });

        // Sync permission state based on actual permission status
        if (
          result.permissionStatus === "granted" ||
          result.permissionStatus === "browser_granted" ||
          result.permissionStatus === "native_granted"
        ) {
          dispatch({ type: BLUEDOT_NATIVE_PERMISSION_GRANTED });
        } else if (
          result.permissionStatus === "denied" ||
          result.permissionStatus === "browser_denied" ||
          result.permissionStatus === "native_denied"
        ) {
          dispatch({
            type: BLUEDOT_NATIVE_PERMISSION_DENIED,
            payload: { status: result.permissionStatus }
          });
        } else {
          dispatch({
            type: BLUEDOT_NATIVE_PERMISSION_CHECK,
            payload: { status: result.permissionStatus || "unknown" }
          });
        }
      } else {
        dispatch({
          type: BLUEDOT_INITIALIZE_FAILURE,
          payload: result.error || "Failed to initialize Bluedot"
        });
      }
    } catch (error) {
      dispatch({
        type: BLUEDOT_INITIALIZE_FAILURE,
        payload: error.message || "Failed to initialize Bluedot"
      });
    }
  };
};

/**
 * Enable location tracking
 *
 * @returns {Function} - Redux thunk
 */
export const enableLocation = () => {
  return async dispatch => {
    dispatch({ type: BLUEDOT_LOCATION_ENABLE });

    try {
      const result = await bluedotService.enableLocation();

      if (result) {
        dispatch({ type: BLUEDOT_LOCATION_ENABLED });
      } else {
        dispatch({
          type: BLUEDOT_LOCATION_FAILURE,
          payload: "Location permission denied or not available"
        });
      }
    } catch (error) {
      dispatch({
        type: BLUEDOT_LOCATION_FAILURE,
        payload: error.message || "Failed to enable location"
      });
    }
  };
};

/**
 * Send a wave event (standalone browser only).
 * In WebView, waves are handled by the native Geo-Triggering service.
 *
 * @param {string} destinationId - Destination ID
 * @param {Object} data - Wave data
 * @returns {Function} - Redux thunk
 */
export const sendWave = (destinationId, data = {}) => {
  return async dispatch => {
    // In WebView context, the native app handles wave-like events via
    // Geo-Triggering (geoTriggeringService.start). Do not use Web SDK.
    if (isWebview()) {
      console.log(
        "[Bluedot] sendWave skipped - native Geo-Triggering handles this"
      );
      return;
    }

    dispatch({ type: BLUEDOT_WAVE_SEND, payload: { destinationId, data } });

    try {
      const result = await bluedotService.sendWave(destinationId, data);

      if (result && result.error) {
        dispatch({
          type: BLUEDOT_WAVE_FAILURE,
          payload: result.error
        });
      } else {
        dispatch({
          type: BLUEDOT_WAVE_SENT,
          payload: { destinationId, data, result }
        });
      }
    } catch (error) {
      dispatch({
        type: BLUEDOT_WAVE_FAILURE,
        payload: error.message || "Failed to send wave"
      });
    }
  };
};

/**
 * Reset Bluedot state
 *
 * @returns {Object}
 */
export const resetBluedot = () => {
  bluedotService.reset();
  return { type: BLUEDOT_RESET };
};

/**
 * Clear error state
 *
 * @returns {Object}
 */
export const clearError = () => {
  return { type: BLUEDOT_ERROR, payload: null };
};

/**
 * Check native location permission status
 * Handles both WebView (native) and standalone (browser) environments
 */
export const checkNativePermission = () => {
  return async dispatch => {
    try {
      // First check browser permission (works in all environments)
      const browserStatus = await checkBrowserPermission();

      if (browserStatus === "granted") {
        dispatch({
          type: BLUEDOT_NATIVE_PERMISSION_GRANTED,
          payload: { status: "browser_granted" }
        });
        return true;
      }

      // If in WebView, also check native permission
      const nativeGranted = await checkNativeLocationPermission();

      if (nativeGranted) {
        dispatch({
          type: BLUEDOT_NATIVE_PERMISSION_GRANTED,
          payload: { status: "native_granted" }
        });
        return true;
      }

      // Permission not granted
      dispatch({
        type: BLUEDOT_NATIVE_PERMISSION_DENIED,
        payload: { status: browserStatus || "denied" }
      });
      return false;
    } catch (error) {
      dispatch({
        type: BLUEDOT_NATIVE_PERMISSION_CHECK,
        payload: { status: "error", error: error.message }
      });
      return false;
    }
  };
};

/**
 * Request location permission
 * Handles both WebView (native) and standalone (browser) environments
 */
export const requestNativePermission = () => {
  return async dispatch => {
    dispatch({ type: BLUEDOT_NATIVE_PERMISSION_CHECK });

    try {
      // Try native permission request first (for WebView)
      const nativeResult = await requestNativeLocationPermission();

      if (nativeResult.granted) {
        dispatch({
          type: BLUEDOT_NATIVE_PERMISSION_GRANTED,
          payload: { status: "native_granted" }
        });
        // Enable Bluedot location after getting permission
        dispatch(enableLocation());
        return { granted: true, position: nativeResult.position };
      }

      // If native request failed, try browser permission as fallback
      const browserStatus = await checkBrowserPermission();

      if (browserStatus === "granted") {
        dispatch({
          type: BLUEDOT_NATIVE_PERMISSION_GRANTED,
          payload: { status: "browser_granted" }
        });
        // Enable Bluedot location after getting permission
        dispatch(enableLocation());
        return { granted: true };
      }

      // Both failed
      dispatch({
        type: BLUEDOT_NATIVE_PERMISSION_DENIED,
        payload: {
          status: browserStatus || "denied",
          error: nativeResult.error
        }
      });
      return {
        granted: false,
        error: nativeResult.error || "Permission denied"
      };
    } catch (error) {
      dispatch({
        type: BLUEDOT_NATIVE_PERMISSION_CHECK,
        payload: { status: "error", error: error.message }
      });
      return { granted: false, error: error.message };
    }
  };
};

/**
 * Send order placement wave (standalone browser only).
 * In WebView, the native app handles order tracking via Geo-Triggering service.
 *
 * @param {Object} orderData - Order information
 * @returns {Function} - Redux thunk
 */
export const sendOrderWave = orderData => {
  return async dispatch => {
    if (isWebview()) {
      console.log(
        "[Bluedot] sendOrderWave skipped - native Geo-Triggering handles order tracking"
      );
      return;
    }

    const waveData = {
      eventType: "orderPlaced",
      orderId: orderData.orderId,
      customerId: orderData.customerId,
      orderTotal: orderData.orderTotal,
      currency: orderData.currency || "USD",
      restaurantName: orderData.restaurantName,
      orderType: orderData.orderType || "delivery",
      timestamp: new Date().toISOString()
    };

    return dispatch(
      sendWave(
        orderData.bluedotDestinationId,
        waveData
      )
    );
  };
};
